import React, { useState, FormEvent, ChangeEvent } from "react";
import axios, { AxiosError } from "axios";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Library } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Props for SaveCharacterModal component
 */
interface SaveCharacterModalProps {
  isOpen: boolean;
  characterName: string;
  portraitUrl: string;
  artStyle: string;
  colorPalette: string;
  onSave: () => void;
  onSkip: () => void;
}

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | undefined | null): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * SaveCharacterModal Component
 *
 * Prompts the user to save a transformed character to their library.
 * Shown after a successful photo transformation.
 */
const SaveCharacterModal: React.FC<SaveCharacterModalProps> = ({
  isOpen,
  characterName,
  portraitUrl,
  artStyle,
  colorPalette,
  onSave,
  onSkip,
}) => {
  const [name, setName] = useState<string>(characterName);
  const [description, setDescription] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const handleSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Please enter a name for the character");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/v1/user-characters`,
        {
          name: name.trim(),
          description: description.trim() || null,
          portrait_url: portraitUrl,
          art_style: artStyle,
          color_palette: colorPalette,
        },
        { withCredentials: true }
      );

      toast.success("Character saved!", {
        description: `${name} has been added to your library.`,
      });
      onSave();
    } catch (error) {
      console.error("Failed to save character:", error);
      const axiosError = error as AxiosError<{ detail?: string }>;
      const message =
        axiosError.response?.data?.detail || "Failed to save character";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.4)",
      }}
      onClick={onSkip}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "var(--st-bg-surface)",
          border: "1px solid var(--st-border-light)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with purple gradient accent */}
        <div
          className="relative px-6 pt-6 pb-4"
          style={{
            background:
              "linear-gradient(180deg, rgba(155, 89, 182, 0.1) 0%, transparent 100%)",
          }}
        >
          {/* Close button */}
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
          </button>

          {/* Icon and title */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "#F3E8FF" }}
            >
              <Library className="w-6 h-6" style={{ color: "#9B59B6" }} />
            </div>
            <div>
              <h2
                className="text-xl font-bold"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Save to Library?
              </h2>
              <p
                className="text-sm"
                style={{ color: "var(--st-text-muted)" }}
              >
                Reuse this character in future stories
              </p>
            </div>
          </div>

          {/* Portrait preview */}
          <div className="flex justify-center">
            <div
              className="relative w-32 h-32 rounded-xl overflow-hidden"
              style={{ border: "3px solid #9B59B6" }}
            >
              <img
                src={getImageUrl(portraitUrl)}
                alt={characterName}
                className="w-full h-full object-cover"
              />
              <div
                className="absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: "#9B59B6" }}
              >
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="px-6 pb-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="character-name"
                className="text-sm font-medium mb-1.5 block"
                style={{ color: "var(--st-text-heading)" }}
              >
                Character Name
              </label>
              <input
                id="character-name"
                type="text"
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="Enter character name"
                className="flex h-11 w-full rounded-xl border px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  borderColor: "var(--st-border-medium)",
                  background: "var(--st-bg-muted)",
                }}
                disabled={saving}
                autoFocus
              />
            </div>

            <div>
              <label
                htmlFor="character-description"
                className="text-sm font-medium mb-1.5 block"
                style={{ color: "var(--st-text-heading)" }}
              >
                Description{" "}
                <span style={{ color: "var(--st-text-muted)" }}>
                  (optional)
                </span>
              </label>
              <textarea
                id="character-description"
                value={description}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                placeholder="e.g., Emma's brave explorer character"
                className="flex min-h-[60px] w-full rounded-xl border px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                style={{
                  borderColor: "var(--st-border-medium)",
                  background: "var(--st-bg-muted)",
                }}
                rows={2}
                disabled={saving}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onSkip}
              className="flex-1 h-11 rounded-xl font-semibold transition-colors hover:opacity-80"
              style={{
                background: "var(--st-bg-muted)",
                color: "var(--st-text-heading)",
              }}
              disabled={saving}
            >
              Skip
            </button>

            <button
              type="submit"
              className="flex-1 h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{
                background: "linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)",
              }}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Library className="w-4 h-4" />
                  Save Character
                </>
              )}
            </button>
          </div>

          {/* Hint */}
          <p
            className="text-xs text-center mt-4"
            style={{ color: "var(--st-text-muted)" }}
          >
            Saved characters can be used instantly in new stories
          </p>
        </form>
      </div>
    </div>
  );
};

export default SaveCharacterModal;
